import {
  Plus, Loader2, MessageCircle, Pin, PinOff, FolderOpen, FolderPlus,
  ChevronDown, Wand2, X, Archive, ArchiveRestore, Trash2, Pencil,
  Merge, Check, Square, CheckSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MicButton } from "@/components/MicButton";
import { ThemeBadge, ThemeFilter } from "@/components/ConversationThemes";

export function ChatSidebar({
  t,
  conversations, activeConv, draftConvIds,
  allTags, tagColors, tagFilter, themeFilter, allThemes,
  folders, activeFolder, expandedFolders, editingFolder, editFolderName,
  showNewFolder, newFolderName, categorizing, folderMenuConv,
  archivedConvs, showArchive,
  searchQuery, searchResults, searching,
  searchRef, filteredConversations,
  // Callbacks
  selectConversation, startNewChat, deleteConversation, togglePin,
  archiveConversation, unarchiveConversation, handleSearch,
  createFolder, deleteFolder, assignFolder, autoCategorize,
  startRenameFolder, renameFolder,
  setTagFilter, setThemeFilter, setActiveFolder,
  setFolderMenuConv, setShowNewFolder, setNewFolderName,
  setEditingFolder, setEditFolderName, setShowArchive,
  setSearchQuery, setSearchResults, setActiveConv,
  // Merge
  mergeMode, setMergeMode, mergeSelection, toggleMergeSelect,
  executeMerge, cancelMerge, merging,
  // Mobile
  mobileOpen, setMobileOpen,
}) {
  const handleSelectConversation = (id) => {
    selectConversation(id);
    if (setMobileOpen) setMobileOpen(false);
  };
  const handleStartNewChat = () => {
    startNewChat();
    if (setMobileOpen) setMobileOpen(false);
  };

  const sidebarContent = (
    <div className={cn(
      "border-r border-border/50 bg-card/30 flex flex-col h-full overflow-hidden",
      "w-full md:w-56 lg:w-72 flex-shrink-0"
    )}>
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <Button
            data-testid="new-chat-btn"
            aria-label="Start new conversation (Ctrl+N)"
            onClick={handleStartNewChat}
            title="New chat (Ctrl+N)"
            className="flex-1 btn-omni bg-primary text-primary-foreground gap-2"
          >
            <Plus className="w-4 h-4" /> {t("chat.newChat")}
            <kbd className="hidden lg:inline-flex ml-auto text-[10px] opacity-60 bg-primary-foreground/15 rounded px-1.5 py-0.5 font-mono">&#8984;N</kbd>
          </Button>
          <button
            data-testid="merge-mode-btn"
            onClick={() => mergeMode ? cancelMerge() : setMergeMode(true)}
            title={mergeMode ? "Cancel merge" : "Merge conversations"}
            className={cn(
              "px-2.5 rounded-xl border-2 transition-all flex items-center justify-center",
              mergeMode
                ? "border-primary bg-primary/10 text-primary"
                : "border-border/50 text-muted-foreground hover:border-primary/30 hover:text-primary"
            )}
          >
            {mergeMode ? <X className="w-4 h-4" /> : <Merge className="w-4 h-4" />}
          </button>
        </div>
        {/* Merge action bar */}
        {mergeMode && (
          <div data-testid="merge-action-bar" className="flex items-center gap-2 px-2 py-2 rounded-xl bg-primary/5 border border-primary/20">
            <span className="text-[11px] font-medium text-primary flex-1">
              {mergeSelection?.size > 0 ? `${mergeSelection.size} selected` : "Tap conversations to select"}
            </span>
            <Button
              data-testid="merge-execute-btn"
              size="sm"
              disabled={!mergeSelection || mergeSelection.size < 2 || merging}
              onClick={executeMerge}
              className="h-7 px-3 text-[11px] bg-primary text-primary-foreground gap-1"
            >
              {merging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Merge className="w-3 h-3" />}
              Merge
            </Button>
          </div>
        )}
        {/* Search */}
        <div className="relative flex items-center gap-1">
          <div className="relative flex-1">
            <input
              data-testid="conversation-search"
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search conversations... (Ctrl+K)"
              className="w-full input-omni border border-input h-10 text-sm pl-9"
            />
            <svg className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            {searching && <Loader2 className="absolute right-3 top-3 w-4 h-4 text-muted-foreground animate-spin" />}
          </div>
          <MicButton onTranscript={(text) => handleSearch(text)} size="sm" />
        </div>
      </div>

      {/* Search Results */}
      {searchResults !== null ? (
        <div className="flex-1 overflow-y-auto px-3 space-y-1">
          {searchResults.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">No results found</p>
          ) : (
            <>
              <p className="text-[10px] text-muted-foreground px-2 mb-2 ui-label">{searchResults.length} conversation{searchResults.length !== 1 ? "s" : ""} found</p>
              {searchResults.map(result => (
                <button
                  key={result.conversation_id}
                  data-testid={`search-result-${result.conversation_id}`}
                  onClick={() => { setActiveConv(result.conversation_id); setSearchQuery(""); setSearchResults(null); }}
                  className="w-full text-left p-3 rounded-2xl hover:bg-muted/50 transition-all"
                >
                  <p className="text-sm font-medium truncate">{result.title}</p>
                  {result.matches.slice(0, 2).map((m, i) => (
                    <p key={i} className="text-[11px] text-muted-foreground mt-1 line-clamp-1 sidebar-text">
                      <span className="font-medium">{m.role === "user" ? "You" : "Omni"}: </span>
                      {m.text}
                    </p>
                  ))}
                </button>
              ))}
            </>
          )}
          <button
            onClick={() => { setSearchQuery(""); setSearchResults(null); }}
            className="w-full text-xs text-primary hover:underline py-2"
          >
            Clear search
          </button>
        </div>
      ) : (
      <>
      {/* Folder filter tabs */}
      <div className="px-3 pb-2 space-y-1">
        <div className="flex items-center justify-between px-1">
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider sidebar-text ui-label">Folders</span>
          <div className="flex items-center gap-1">
            <button
              data-testid="auto-categorize-btn"
              onClick={autoCategorize}
              disabled={categorizing}
              className="p-1 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-primary transition-all disabled:opacity-50"
              title="Auto-categorize with AI"
            >
              {categorizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            </button>
            <button
              data-testid="new-folder-btn"
              onClick={() => setShowNewFolder(!showNewFolder)}
              className="p-1 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-primary transition-all"
              title="New folder"
            >
              <FolderPlus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        {showNewFolder && (
          <div className="flex items-center gap-1">
            <input
              data-testid="new-folder-input"
              type="text"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && createFolder()}
              placeholder="Folder name..."
              className="flex-1 input-omni border border-input h-8 text-xs px-2"
              autoFocus
            />
            <button onClick={createFolder} className="p-1.5 rounded-lg bg-primary text-primary-foreground">
              <Plus className="w-3 h-3" />
            </button>
            <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); }} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <button
          data-testid="folder-all"
          onClick={() => setActiveFolder(null)}
          className={cn("w-full flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs transition-all sidebar-text",
            activeFolder === null ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted/30"
          )}
        >
          <MessageCircle className="w-3.5 h-3.5" /> All
          <span className="ml-auto text-[10px] opacity-60 ui-label">{conversations.length}</span>
        </button>
        {folders.map(f => (
          <div
            key={f.id}
            data-testid={`folder-${f.id}`}
            onClick={() => setActiveFolder(activeFolder === f.id ? null : f.id)}
            className={cn("w-full group flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs transition-all cursor-pointer sidebar-text",
              activeFolder === f.id ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted/30"
            )}
          >
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: f.color }} />
            {editingFolder === f.id ? (
              <input
                data-testid={`rename-folder-input-${f.id}`}
                type="text"
                value={editFolderName}
                onChange={e => setEditFolderName(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") renameFolder(f.id); if (e.key === "Escape") setEditingFolder(null); }}
                onBlur={() => renameFolder(f.id)}
                onClick={e => e.stopPropagation()}
                className="flex-1 bg-transparent border-b border-primary outline-none text-xs min-w-0"
                autoFocus
              />
            ) : (
              <span
                className="truncate flex-1 text-left"
                onDoubleClick={(e) => startRenameFolder(f, e)}
                title="Double-click to rename"
              >
                {f.name}
              </span>
            )}
            <span className="text-[10px] opacity-60 ui-label">{f.count}</span>
            <button
              data-testid={`delete-folder-${f.id}`}
              onClick={(e) => deleteFolder(f.id, e)}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-destructive transition-all"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {folders.length > 0 && (
          <button
            data-testid="folder-uncategorized"
            onClick={() => setActiveFolder(activeFolder === "uncategorized" ? null : "uncategorized")}
            className={cn("w-full flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs transition-all sidebar-text",
              activeFolder === "uncategorized" ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-muted/30"
            )}
          >
            <FolderOpen className="w-3.5 h-3.5" /> Uncategorized
          </button>
        )}
      </div>
      {/* Tag filter chips */}
      {allTags.length > 0 && (
        <div className="px-3 pt-1 pb-0.5 flex flex-wrap gap-1">
          {allTags.map(tag => {
            const c = tagColors.find(tc => tc.name === tag.color) || {};
            return (
              <button
                key={tag.id}
                data-testid={`tag-filter-${tag.id}`}
                onClick={() => setTagFilter(tagFilter === tag.id ? null : tag.id)}
                className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium transition-all border",
                  tagFilter === tag.id ? "ring-1 ring-primary" : "opacity-70 hover:opacity-100"
                )}
                style={{ background: c.bg, color: c.text, borderColor: c.text + "33" }}
              >
                {tag.label}
              </button>
            );
          })}
          {tagFilter && (
            <button onClick={() => setTagFilter(null)} className="px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground">
              Clear
            </button>
          )}
        </div>
      )}
      {/* Theme Filter */}
      {allThemes.length > 0 && (
        <div className="px-3 py-1">
          <ThemeFilter
            themes={allThemes}
            activeTheme={themeFilter}
            onSelect={setThemeFilter}
            onClear={() => setThemeFilter(null)}
          />
        </div>
      )}
      <div className="border-t border-border/30 mx-3" />
      <div className="flex-1 overflow-y-auto px-3 space-y-1 pt-1">
        {filteredConversations.map(conv => (
          <div
            key={conv.id}
            data-testid={`conv-${conv.id}`}
            onClick={() => mergeMode ? toggleMergeSelect(conv.id) : handleSelectConversation(conv.id)}
            className={cn(
              "group flex items-center gap-3 px-4 py-3 rounded-2xl cursor-pointer transition-all",
              mergeMode && mergeSelection?.has(conv.id)
                ? "bg-primary/15 text-primary ring-2 ring-primary/30"
                : activeConv === conv.id && !mergeMode
                ? "bg-primary/10 text-primary"
                : "hover:bg-muted/50 text-muted-foreground"
            )}
          >
            {mergeMode ? (
              mergeSelection?.has(conv.id)
                ? <CheckSquare className="w-4 h-4 flex-shrink-0 text-primary" strokeWidth={2} />
                : <Square className="w-4 h-4 flex-shrink-0 text-muted-foreground/50" strokeWidth={1.5} />
            ) : conv.pinned ? (
              <Pin className="w-4 h-4 flex-shrink-0 text-primary" strokeWidth={1.5} />
            ) : draftConvIds.has(conv.id) ? (
              <Pencil className="w-4 h-4 flex-shrink-0 text-primary/60" strokeWidth={1.5} data-testid={`draft-indicator-${conv.id}`} />
            ) : (
              <MessageCircle className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            )}
            <div className="flex flex-col min-w-0 flex-1">
              <span className={cn("text-sm truncate sidebar-text", conv.pinned && "font-medium")}>{conv.title || "New chat"}</span>
              {(conv.tags || []).length > 0 && (
                <div className="flex gap-1 mt-0.5">
                  {(conv.tags || []).slice(0, 2).map(tid => {
                    const tag = allTags.find(t => t.id === tid);
                    if (!tag) return null;
                    const c = tagColors.find(tc => tc.name === tag.color) || {};
                    return <span key={tid} className="text-[8px] px-1.5 py-px rounded-full" style={{ background: c.bg, color: c.text }}>{tag.label}</span>;
                  })}
                  {(conv.tags || []).length > 2 && <span className="text-[8px] text-muted-foreground">+{(conv.tags || []).length - 2}</span>}
                </div>
              )}
              {(conv.themes || []).length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {(conv.themes || []).slice(0, 2).map(t => (
                    <ThemeBadge key={t} themeKey={t} size="xs" />
                  ))}
                </div>
              )}
            </div>
            <div className={cn("flex items-center gap-0.5 flex-shrink-0", mergeMode && "hidden")}>
              {/* Folder assign menu */}
              <div className="relative">
                <button
                  data-testid={`folder-assign-${conv.id}`}
                  onClick={(e) => { e.stopPropagation(); setFolderMenuConv(folderMenuConv === conv.id ? null : conv.id); }}
                  className="opacity-0 group-hover:opacity-100 transition-all p-1 rounded-lg text-muted-foreground hover:text-primary"
                  title="Move to folder"
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                </button>
                {folderMenuConv === conv.id && (
                  <div
                    className="absolute left-0 top-full mt-1 z-50 w-44 bg-card border border-border/50 rounded-xl shadow-lg py-1 animate-in fade-in slide-in-from-top-1 duration-150"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => assignFolder(conv.id, null)}
                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center gap-2"
                    >
                      <X className="w-3 h-3" /> None
                    </button>
                    {folders.map(f => (
                      <button
                        key={f.id}
                        onClick={() => assignFolder(conv.id, f.id)}
                        className={cn(
                          "w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 flex items-center gap-2",
                          conv.folder_id === f.id && "text-primary font-medium"
                        )}
                      >
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: f.color }} />
                        {f.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                data-testid={`pin-conv-${conv.id}`}
                onClick={(e) => togglePin(conv.id, e)}
                className={cn(
                  "transition-all p-1 rounded-lg",
                  conv.pinned
                    ? "text-primary hover:text-primary/70"
                    : "opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary"
                )}
                title={conv.pinned ? "Unpin" : "Pin"}
              >
                {conv.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
              </button>
              <button
                data-testid={`archive-conv-${conv.id}`}
                onClick={(e) => archiveConversation(conv.id, e)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-amber-600 p-1 rounded-lg"
                title="Archive"
              >
                <Archive className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`delete-conv-${conv.id}`}
                onClick={(e) => deleteConversation(conv.id, e)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-1 rounded-lg"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {/* Archive Section */}
      {archivedConvs.length > 0 && (
        <>
        <div className="border-t border-border/30 mx-3 mt-1" />
        <div className="px-3 py-2">
          <button
            data-testid="toggle-archive"
            onClick={() => setShowArchive(!showArchive)}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs text-muted-foreground hover:bg-muted/30 transition-all sidebar-text"
          >
            <Archive className="w-3.5 h-3.5" />
            <span>Archive</span>
            <span className="ml-auto text-[10px] opacity-60 ui-label">{archivedConvs.length}</span>
            <ChevronDown className={cn("w-3 h-3 transition-transform", showArchive && "rotate-180")} />
          </button>
          {showArchive && (
            <div className="mt-1 space-y-1">
              {archivedConvs.map(conv => (
                <div
                  key={conv.id}
                  data-testid={`archived-${conv.id}`}
                  className="group flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-muted-foreground/70 hover:bg-muted/30 transition-all sidebar-text"
                >
                  <MessageCircle className="w-3.5 h-3.5 flex-shrink-0 opacity-50" />
                  <span className="truncate flex-1">{conv.title || "Untitled"}</span>
                  <button
                    data-testid={`unarchive-${conv.id}`}
                    onClick={() => unarchiveConversation(conv.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:text-primary transition-all"
                    title="Restore"
                  >
                    <ArchiveRestore className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
      )}
      </>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-shrink-0">
        {sidebarContent}
      </div>
      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative w-[85vw] max-w-xs h-full animate-in slide-in-from-left duration-200">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}
